import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Activity,
  Clock,
  Coins,
  LayoutGrid,
  Heart,
  Swords,
  Zap,
} from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import MarketHeroAvatar from "../../../components/market/MarketHeroAvatar";
import ContractLineAvatar from "../../../components/market/ContractLineAvatar";
import { isOutcomeTradable, h2hTitleDuplicatesMatchup } from "../../utils/bondPredictDetailHelpers";
import {
  formatChancePct,
  formatPrice,
  formatShortCountdown,
  formatVolume,
  getBinaryOdds,
  getH2HLabels,
  getMarketTitle,
  getOutcomeDisplayPrice,
  groupOutcomesBySelection,
  MARKET_TYPES,
  STATUS_META,
  TYPE_META,
} from "../../utils/bondPredictHelpers";

const outcomePickProps = (outcome, market, variant, onOutcomeClick, userId) => {
  const tradable = isOutcomeTradable(market, outcome);
  const disabled = !outcome?._id || !tradable;
  return {
    type: "button",
    disabled,
    className: `bp-card-outcome-btn ${variant}${disabled ? " bp-card-outcome-btn-disabled" : ""}`,
    title: !userId
      ? "Sign in to trade"
      : !tradable
        ? "Trading not available"
        : "Trade this outcome",
    onClick: (e) => {
      e.stopPropagation();
      if (!outcome?._id) return;
      onOutcomeClick?.(outcome, market);
    },
  };
};

const BinaryBody = ({ market, userId, onOutcomeClick }) => {
  const { yes, no, yesPct, noPct, yesPrice, noPrice } = getBinaryOdds(market);
  if (!yes || !no) return null;
  const showBar = yesPct != null && noPct != null && yesPct + noPct > 0;
  const flexY = showBar ? yesPct : 1;
  const flexN = showBar ? noPct : 1;

  return (
    <div className="bp-card-binary">
      <div className="bp-card-binary-picks">
        <button {...outcomePickProps(yes, market, "bp-card-pick bp-card-pick-yes", onOutcomeClick, userId)}>
          <span className="bp-card-pick-label">Yes</span>
          <strong>{formatChancePct(yesPrice)}</strong>
        </button>
        <button {...outcomePickProps(no, market, "bp-card-pick bp-card-pick-no", onOutcomeClick, userId)}>
          <span className="bp-card-pick-label">No</span>
          <strong>{formatChancePct(noPrice)}</strong>
        </button>
      </div>
      {showBar ? (
        <div className="bp-card-prob-bar" aria-hidden>
          <span style={{ flex: flexY }} className="bp-card-prob-yes" />
          <span style={{ flex: flexN }} className="bp-card-prob-no" />
        </div>
      ) : null}
    </div>
  );
};

const H2HBody = ({ market, userId, onOutcomeClick }) => {
  const h2h = getH2HLabels(market);
  const grouped = groupOutcomesBySelection(market?.outcomes || []);
  const g = grouped.find((x) => x.yes && x.no);
  if (!h2h || !g) return null;

  return (
    <div className="bp-card-h2h">
      <button
        {...outcomePickProps(g.yes, market, "bp-card-h2h-side", onOutcomeClick, userId)}
      >
        <img
          src={h2h.logoA || generateAvatarUrl(h2h.labelA)}
          alt=""
          className="bp-card-h2h-logo"
        />
        <span className="bp-card-h2h-name">{h2h.labelA}</span>
        <strong>{formatPrice(h2h.priceA)}</strong>
      </button>
      <div className="bp-card-h2h-vs">
        <span>{h2h.fxStatus}</span>
      </div>
      <button
        {...outcomePickProps(g.no, market, "bp-card-h2h-side bp-card-h2h-side-right", onOutcomeClick, userId)}
      >
        <img
          src={h2h.logoB || generateAvatarUrl(h2h.labelB)}
          alt=""
          className="bp-card-h2h-logo"
        />
        <span className="bp-card-h2h-name">{h2h.labelB}</span>
        <strong>{formatPrice(h2h.priceB)}</strong>
      </button>
    </div>
  );
};

const MultiBody = ({ market, userId, onOutcomeClick }) => {
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter((g) => g.yes || g.no);
  const lines = grouped.slice(0, 3);
  if (!lines.length) return null;

  return (
    <div className="bp-card-multi">
      {lines.map((g) => {
        const outcome = g.yes || g.no;
        const label = (g.yes?.label || g.no?.label || g.selectionKey || "").replace(
          /\s*\((Yes|No)\)\s*$/i,
          ""
        );
        return (
          <button
            key={g.selectionKey}
            {...outcomePickProps(outcome, market, "bp-card-multi-row", onOutcomeClick, userId)}
          >
            <ContractLineAvatar
              market={market}
              selectionKey={g.selectionKey}
              label={label}
              size={18}
              className="bp-card-multi-thumb"
            />
            <span className="bp-card-multi-label">{label}</span>
            <span className="bp-card-multi-pct">{formatPrice(getOutcomeDisplayPrice(outcome))}</span>
          </button>
        );
      })}
      {grouped.length > lines.length ? (
        <span className="bp-card-multi-more">+ more outcomes</span>
      ) : null}
    </div>
  );
};

const typeIcon = (type) => {
  if (type === MARKET_TYPES.HEAD_TO_HEAD) return Swords;
  if (type === MARKET_TYPES.MULTI_BINARY) return LayoutGrid;
  return Zap;
};

const PredictMarketCard = ({
  market,
  variant = "grid",
  active,
  isLiked,
  likeBusy,
  onSelect,
  onToggleLike,
  onOutcomeClick,
  userId,
}) => {
  const [countdown, setCountdown] = useState("");
  const [titleExpanded, setTitleExpanded] = useState(false);
  const [titleTruncated, setTitleTruncated] = useState(false);
  const titleRef = useRef(null);

  const type = market?.type || MARKET_TYPES.BINARY;
  const status = market?.status || "OPEN";
  const statusMeta = STATUS_META[status] || { label: status, tone: "muted" };
  const typeMeta = TYPE_META[type] || TYPE_META.BINARY;
  const TypeIcon = typeIcon(type);
  const title = getMarketTitle(market);
  const closeAt = market?.timings?.closeAt;
  const volume = market?.metrics?.totalVolume || 0;
  const trades = market?.metrics?.totalTrades || 0;
  const likes = market?.likes?.count || 0;
  const category = market?.category;
  const leagueName = market?.fixture?.league?.name?.trim() || "";
  const leagueLogo = market?.fixture?.league?.logo || null;
  const homeName = market?.fixture?.teams?.home?.name;
  const awayName = market?.fixture?.teams?.away?.name;
  const hideH2hTitle =
    type === MARKET_TYPES.HEAD_TO_HEAD &&
    homeName &&
    awayName &&
    h2hTitleDuplicatesMatchup(title, homeName, awayName);
  const showTitleToggle = !hideH2hTitle && (titleExpanded || titleTruncated);

  const checkTitleTruncation = useCallback(() => {
    const el = titleRef.current;
    if (!el) {
      setTitleTruncated(false);
      return;
    }
    setTitleTruncated(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useEffect(() => {
    setTitleExpanded(false);
  }, [market?._id]);

  useLayoutEffect(() => {
    if (titleExpanded) return;
    checkTitleTruncation();
  }, [title, titleExpanded, checkTitleTruncation]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(checkTitleTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTitleTruncation]);

  useEffect(() => {
    if (!closeAt) {
      setCountdown("");
      return undefined;
    }
    const tick = () => setCountdown(formatShortCountdown(closeAt, status));
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, [closeAt, status]);

  const marketEyebrow = (
    <div className="bp-market-card-eyebrow">
      <TypeIcon size={10} strokeWidth={2.5} />
      <span>{typeMeta.short}</span>
      {type === MARKET_TYPES.HEAD_TO_HEAD && leagueName ? (
        <>
          <span className="bp-market-card-dot" />
          {leagueLogo ? (
            <img src={leagueLogo} alt="" className="bp-market-card-league-logo" />
          ) : null}
          <span className="bp-market-card-eyebrow-league">{leagueName}</span>
        </>
      ) : category ? (
        <>
          <span className="bp-market-card-dot" />
          <span>{category.replace(/_/g, " ")}</span>
        </>
      ) : null}
    </div>
  );

  return (
    <article
      className={`bp-market-card${variant === "rail" ? " bp-market-card-rail" : ""}${active ? " bp-market-card-active" : ""}${type !== MARKET_TYPES.HEAD_TO_HEAD ? " bp-market-card-has-hero" : ""}${hideH2hTitle ? " bp-market-card-h2h-compact" : ""}`}
      onClick={() => onSelect?.(market)}
    >
      <div className="bp-market-card-shine" aria-hidden />

      <header className="bp-market-card-head">
        {type !== MARKET_TYPES.HEAD_TO_HEAD ? (
          <MarketHeroAvatar market={market} label={title} size={36} className="bp-market-card-avatar" />
        ) : null}
        <div className="bp-market-card-head-content">
          {hideH2hTitle ? marketEyebrow : null}
          {!hideH2hTitle ? (
            <>
              <h3
                ref={titleRef}
                className={`bp-market-card-title${titleExpanded ? " bp-market-card-title-expanded" : ""}`}
              >
                {title}
              </h3>
              {showTitleToggle ? (
                <button
                  type="button"
                  className="bp-market-card-read-more"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTitleExpanded((v) => !v);
                  }}
                >
                  {titleExpanded ? "Show less" : "Read More"}
                </button>
              ) : null}
            </>
          ) : null}
          {!hideH2hTitle ? marketEyebrow : null}
        </div>
        <div className="bp-market-card-badges">
          <span className={`bp-status-pill bp-status-${statusMeta.tone}`}>
            <span className="bp-status-dot" />
            {statusMeta.label}
          </span>
          {countdown ? (
            <span className="bp-countdown-chip">
              <Clock size={9} strokeWidth={2.5} />
              {countdown}
            </span>
          ) : null}
        </div>
      </header>

      <div className="bp-market-card-body">
        {type === MARKET_TYPES.BINARY && (
          <BinaryBody market={market} userId={userId} onOutcomeClick={onOutcomeClick} />
        )}
        {type === MARKET_TYPES.HEAD_TO_HEAD && (
          <H2HBody market={market} userId={userId} onOutcomeClick={onOutcomeClick} />
        )}
        {type === MARKET_TYPES.MULTI_BINARY && (
          <MultiBody market={market} userId={userId} onOutcomeClick={onOutcomeClick} />
        )}
      </div>

      <footer className="bp-market-card-foot">
        <span className="bp-market-card-stat">
          <Coins size={11} strokeWidth={2.25} />
          <strong>{formatVolume(volume)}</strong>
          <span>vol</span>
        </span>
        <span className="bp-market-card-stat">
          <Activity size={11} strokeWidth={2.25} />
          <strong>{trades}</strong>
          <span>trades</span>
        </span>
        <button
          type="button"
          className={`bp-like-btn${isLiked ? " bp-like-btn-on" : ""}`}
          disabled={likeBusy}
          onClick={(e) => {
            e.stopPropagation();
            onToggleLike?.(market);
          }}
          aria-label={isLiked ? "Unlike market" : "Like market"}
        >
          <Heart size={12} strokeWidth={2.25} fill={isLiked ? "currentColor" : "none"} />
          <span>{isLiked ? "Liked" : "Like"}</span>
          {likes > 0 ? <span className="bp-like-count">{likes}</span> : null}
        </button>
      </footer>
    </article>
  );
};

export default PredictMarketCard;
